import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading, Spinner } from '@chakra-ui/react';
import TokenService from '../../services/token/TokenService';
const tokenService = new TokenService();

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday','Varies'];
const GAME_TYPES = ["Texas Hold'em",'Omaha','Omaha Hi-Lo','Mixed Games',"Dealer's Choice",'Other'];
const BUY_IN_RANGES = ['Free','Under $20','$20-$50','$50-$100','$100-$200','$200-$500','$500+','Varies'];
const CONTACT_METHODS = ['PokerCity Message','Phone/Text','Email','Facebook','Discord','Telegram','Other'];
const BLIND_OPTIONS = ['$0.25/$0.50','$0.50/$1','$1/$2','$2/$5','$5/$10','$10/$25','Tournament levels','Other'];

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };

const inp: React.CSSProperties = { display:'block',width:'100%',padding:'12px 14px',background:'#0D1118',border:`1px solid #1E2330`,borderRadius:8,color:'#F0F4FF',fontSize:15,fontFamily:"'Rajdhani','Arial',sans-serif",boxSizing:'border-box' as any };
const lbl: React.CSSProperties = { display:'block',fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#19E6CF',letterSpacing:'0.15em',marginBottom:6,textTransform:'uppercase' as const };

const SubmitGamePage: FC = () => {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    privacy: 'public', name:'',area:'',city:'',province:'',day:'',time:'',game_type:'',buy_in_range:'',blinds:'',max_players:'',contact_method:'',contact_value:'',notes:'',recurring:true });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [lat, setLat] = useState<number|null>(null);
  const [lng, setLng] = useState<number|null>(null);
  const [geocoding, setGeocoding] = useState(false);

  const set = (f: string, v: any) => setForm(prev => ({ ...prev, [f]: v }));

  const geocodeCity = async () => {
    if (!form.city || !form.province) return;
    setGeocoding(true);
    try {
      const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(`${form.city}, ${form.province}, Canada`)}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`);
      const data = await resp.json();
      if (data.results?.[0]?.geometry?.location) {
        setLat(data.results[0].geometry.location.lat);
        setLng(data.results[0].geometry.location.lng);
      }
    } catch {}
    setGeocoding(false);
  };

  const handleSubmit = async () => {
    if (!user?.uid) { setError('You must be signed in.'); return; }

    // Passport required to submit
    try {
      const { getDoc: gd, doc: d2 } = await import('firebase/firestore');
      const { db: database } = await import('../../firebase');
      const uSnap = await gd(d2(database, 'users', user.uid));
      const passportNumber = uSnap.data()?.passport_number;
      if (!passportNumber) {
        setError('You need a Player Passport to submit games. Visit your Passport page to get one — it is free.');
        setSubmitting(false); return;
      }
    } catch(e) { console.error('Passport check error:', e); }

    // Must be a public/recurring game
    if (form.privacy === 'private') {
      setError('Only public or sanctioned games can be listed in the directory. Private home games cannot be submitted.');
      setSubmitting(false); return;
    }
    if (!form.name || !form.city || !form.day || !form.game_type || !form.buy_in_range) { setError('Please fill in all required fields.'); return; }
    setSubmitting(true); setError('');
    try {
      // 5 token cost for game submission
      const tokResult = await tokenService.deductTokensForAction(user.uid, 5, 'bonus' as any, 'Game directory submission — 5 PC tokens');
      if (!tokResult.success) { setError('You need 5 PC tokens to submit a game to the directory.'); setSubmitting(false); return; }
      const existing = await getDocs(query(collection(db,'games'),where('submitter_uid','==',user.uid),where('name','==',form.name)));
      if (!existing.empty) { setError('You have already submitted a game with this name.'); setSubmitting(false); return; }
      await addDoc(collection(db,'games'), {
        name:form.name, area:form.area, city:form.city, province:form.province,
        lat:lat??null, lng:lng??null,
        schedule:{ day:form.day, time:form.time, recurring:form.recurring },
        game_type:form.game_type, buy_in_range:form.buy_in_range, blinds:form.blinds,
        max_players:form.max_players?parseInt(form.max_players):null,
        contact_method:form.contact_method, contact_value:form.contact_value, notes:form.notes,
        submitter_uid:user.uid, submitter_name:user.displayName??'Anonymous',
        submitted_at:serverTimestamp(), verification_level:0, verification_score:1,
        status:'active', claimed_by_uid:null, td_uid:null, corroborations:[], corroboration_count:0,
      });
      setSubmitted(true);
    } catch(e) { console.error(e); setError('Something went wrong. Please try again.'); }
    setSubmitting(false);
  };

  if (submitted) return (
    <Box maxW="600px" mx="auto" px={4} py={8} fontFamily={F.ui}>
      <Box bg={C.card} border={`2px solid ${C.green}`} borderRadius={10} p={8} textAlign="center">
        <Text style={{fontSize:52,marginBottom:16}}>✅</Text>
        <Heading style={{fontFamily:F.ui,color:C.white,fontSize:26,marginBottom:8}}>Game Submitted!</Heading>
        <Text style={{fontFamily:F.ui,fontSize:14,color:C.ink,lineHeight:1.7,marginBottom:8}}>
          Your submission is now listed as <b style={{color:C.teal}}>Community Submitted</b> in the game directory.
        </Text>
        <Text style={{fontFamily:F.ui,fontSize:13,color:C.dim,lineHeight:1.7,marginBottom:24}}>
          If the TD claims and verifies this game, you'll earn <b style={{color:C.gold}}>25 PC Tokens</b> as the first submitter.
        </Text>
        <Flex gap={3} justify="center">
          <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534" borderRadius={8}
            fontSize="13px" fontWeight="700" color="#86EFAC" fontFamily={F.ui} cursor="pointer"
            onClick={()=>navigate('/find-a-game')}>View Game Directory →</Box>
          <Box as="button" px={5} py={3} bg={C.card} border={`1px solid ${C.border}`} borderRadius={8}
            fontSize="13px" fontWeight="700" color={C.dim} fontFamily={F.ui} cursor="pointer"
            onClick={()=>{setSubmitted(false);setForm({name:'',area:'',city:'',province:'',day:'',time:'',game_type:'',buy_in_range:'',blinds:'',max_players:'',contact_method:'',contact_value:'',notes:'',recurring:true});}}>
            Submit Another</Box>
        </Flex>
      </Box>
    </Box>
  );

  return (
    <Box maxW="600px" mx="auto" px={4} py={8} fontFamily={F.ui}>
      <Heading style={{fontFamily:F.ui,color:C.white,fontSize:26,marginBottom:6}}>Submit a Game</Heading>
      <Text style={{fontFamily:F.ui,fontSize:13,color:C.dim,lineHeight:1.6,marginBottom:20}}>
        Know of a regular poker game? Add it to the PokerCity directory. No exact address required.
      </Text>

      <Box bg="#1A0F00" border={`1px solid ${C.gold}`} borderRadius={8} p={3} mb={6}>
        <Flex align="center" gap={3}>
          <Text style={{fontSize:20}}>🏆</Text>
          <Text style={{fontFamily:F.ui,fontSize:13,color:C.ink,lineHeight:1.6}}>
            <b style={{color:C.gold}}>Earn 25 PC Tokens</b> if you're the first to submit a game and the TD later claims and verifies it.
          </Text>
        </Flex>
      </Box>

      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} overflow="hidden">
        <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
          <Text style={{fontFamily:F.mono,fontSize:11,color:C.teal,letterSpacing:'0.2em'}}>GAME DETAILS</Text>
        </Box>
        <Box p={5}>
          <Flex direction="column" gap={5}>

            <Box>
              <label style={lbl}>Game Name *</label>
              <input style={inp} placeholder="e.g. Thursday Night Hold'em" value={form.name} onChange={e=>set('name',e.target.value)} />
            </Box>

            <Flex gap={3}>
              <Box flex={1}>
                <label style={lbl}>City / Town *</label>
                <input style={inp} placeholder="e.g. Winnipeg" value={form.city} onChange={e=>set('city',e.target.value)} onBlur={geocodeCity} />
              </Box>
              <Box flex={1}>
                <label style={lbl}>Province *</label>
                <input style={inp} placeholder="e.g. MB" value={form.province} onChange={e=>set('province',e.target.value)} onBlur={geocodeCity} />
              </Box>
            </Flex>

            <Box>
              <label style={lbl}>Area / Neighbourhood</label>
              <input style={inp} placeholder="e.g. River Heights (no exact address)" value={form.area} onChange={e=>set('area',e.target.value)} />
              {geocoding && <Text style={{fontFamily:F.ui,fontSize:12,color:C.dim,marginTop:4}}>Locating on map...</Text>}
              {lat && !geocoding && <Text style={{fontFamily:F.mono,fontSize:11,color:C.green,marginTop:4}}>✓ Location found</Text>}
            </Box>

            <Flex gap={3}>
              <Box flex={1}>
                <label style={lbl}>Day *</label>
                <select style={{...inp,cursor:'pointer'}} value={form.day} onChange={e=>set('day',e.target.value)}>
                  <option value="">Select day...</option>
                  {DAYS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </Box>
              <Box flex={1}>
                <label style={lbl}>Start Time</label>
                <input style={inp} type="time" value={form.time} onChange={e=>set('time',e.target.value)} />
              </Box>
            </Flex>

            <Flex align="center" gap={3}>
              <Box as="button" type="button" w="44px" h="24px" borderRadius="full"
                bg={form.recurring?C.teal:C.border} position="relative" cursor="pointer"
                onClick={()=>set('recurring',!form.recurring)} transition="background 0.2s">
                <Box position="absolute" top="2px" left={form.recurring?'22px':'2px'} w="20px" h="20px"
                  borderRadius="full" bg="white" transition="left 0.2s" />
              </Box>
              <Text style={{fontFamily:F.ui,fontSize:13,color:C.ink}}>
                {form.recurring?'Recurring weekly game':'One-time or irregular game'}
              </Text>
            </Flex>

            <Flex gap={3}>
              <Box flex={1}>
                <label style={lbl}>Game Type *</label>
                <select style={{...inp,cursor:'pointer'}} value={form.game_type} onChange={e=>set('game_type',e.target.value)}>
                  <option value="">Select...</option>
                  {GAME_TYPES.map(g=><option key={g} value={g}>{g}</option>)}
                </select>
              </Box>
              <Box flex={1}>
                <label style={lbl}>Buy-in Range *</label>
                <select style={{...inp,cursor:'pointer'}} value={form.buy_in_range} onChange={e=>set('buy_in_range',e.target.value)}>
                  <option value="">Select...</option>
                  {BUY_IN_RANGES.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
              </Box>
            </Flex>

            <Flex gap={3}>
              <Box flex={1}>
                <label style={lbl}>Blinds / Structure</label>
                <select style={{...inp,cursor:'pointer'}} value={form.blinds} onChange={e=>set('blinds',e.target.value)}>
                  <option value="">Select...</option>
                  {BLIND_OPTIONS.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
              </Box>
              <Box flex={1}>
                <label style={lbl}>Max Players</label>
                <input style={inp} type="number" placeholder="e.g. 9" value={form.max_players} onChange={e=>set('max_players',e.target.value)} />
              </Box>
            </Flex>

            <Flex gap={3}>
              <Box flex={1}>
                <label style={lbl}>Contact Method</label>
                <select style={{...inp,cursor:'pointer'}} value={form.contact_method} onChange={e=>set('contact_method',e.target.value)}>
                  <option value="">Select...</option>
                  {CONTACT_METHODS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </Box>
              <Box flex={2}>
                <label style={lbl}>Contact Info</label>
                <input style={inp} placeholder="Phone, username, link..." value={form.contact_value} onChange={e=>set('contact_value',e.target.value)} />
              </Box>
            </Flex>

            <Box>
              <label style={lbl}>Additional Notes</label>
              <Box as="textarea" style={{...inp,resize:'none' as any}} rows={3}
                placeholder="Anything else players should know..."
                value={form.notes} onChange={(e:any)=>set('notes',e.target.value)} />
            </Box>

            {error && <Box bg="#2D0D0D" border={`1px solid ${C.red}`} borderRadius={6} p={3}>
              <Text style={{fontFamily:F.ui,fontSize:13,color:'#FCA5A5'}}>{error}</Text>
            </Box>}

            <Box as="button" px={6} py={4}
              bg={submitting?C.card:'#052E16'} border={`1px solid ${submitting?C.border:'#166534'}`}
              borderRadius={8} fontSize="15px" fontWeight="700"
              color={submitting?C.dim:'#86EFAC'} fontFamily={F.ui}
              cursor={submitting?'wait':'pointer'} opacity={submitting?0.7:1}
              onClick={handleSubmit} disabled={submitting}>
              {submitting?'Submitting...':'Submit Game to Directory →'}
            </Box>
          </Flex>
        </Box>
      </Box>
      <Text style={{fontFamily:F.ui,fontSize:11,color:C.dim,marginTop:12,textAlign:'center'}}>
        Submissions are reviewed by the community. Inaccurate or spam submissions will be removed.
      </Text>
    </Box>
  );
};

export default SubmitGamePage;
